import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardShell from '../../components/dashboard/DashboardShell';
import { supabase } from '../../lib/supabaseClient';
import type { TattooEntry, Payment } from '../../types/dashboard';
import { athensTime, combineDateTimeAthens, formatCurrency, getBestDayPayout, getBestMonthPayout, getCycleRange, groupEntriesByDate, sumEntries, sumPayout, toDateInputValue, toTimeInputValue } from '../../lib/dashboardUtils';
import { createEntry, deleteEntry, fetchEntries, fetchPayments, updateEntry } from '../../lib/dashboardApi';

export default function ArtistDashboard() {
  const { state } = useAuth();
  const user = state.user;
  const artistId = user?.id;

  const [entries, setEntries] = useState<TattooEntry[]>([]);
  const [statsEntries, setStatsEntries] = useState<TattooEntry[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const [date, setDate] = useState(() => toDateInputValue(new Date()));
  const [time, setTime] = useState(() => toTimeInputValue(new Date()));
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'split'>('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [tipEur, setTipEur] = useState('');
  const [ownCustomer, setOwnCustomer] = useState(false);
  const [workPeriod, setWorkPeriod] = useState<'inside' | 'outside' | 'external'>('inside');
  const [showSpecial, setShowSpecial] = useState(false);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState<TattooEntry | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TattooEntry | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  const artistRate = user?.artistRate ?? 0.2;
  const cycleRange = getCycleRange(user?.paymentCycleAnchor, new Date());

  useEffect(() => {
    if (!artistId) return;
    let active = true;
    async function loadAll() {
      setLoading(true);
      setError(null);
      try {
        const [entryRows, statsRows, paymentRows] = await Promise.all([
          fetchEntries({ artistId, from: from || undefined, to: to || undefined, search: search || undefined }),
          fetchEntries({ artistId }),
          fetchPayments(artistId),
        ]);
        if (!active) return;
        setEntries(entryRows);
        setStatsEntries(statsRows);
        setPayments(paymentRows);
      } catch (err: any) {
        if (!active) return;
        setError(err?.message || 'Failed to load data.');
      } finally {
        if (active) setLoading(false);
      }
    }
    loadAll();
    return () => { active = false; };
  }, [artistId, from, to, search]);

  useEffect(() => {
    if (!artistId) return;
    const channel = supabase
      .channel(`artist-${artistId}-realtime`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tattoo_entries', filter: `artist_id=eq.${artistId}` }, () => {
        fetchEntries({ artistId, from: from || undefined, to: to || undefined, search: search || undefined }).then(setEntries).catch(() => {});
        fetchEntries({ artistId }).then(setStatsEntries).catch(() => {});
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payments', filter: `artist_id=eq.${artistId}` }, () => {
        fetchPayments(artistId).then(setPayments).catch(() => {});
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [artistId, from, to, search]);


  const recentWeekPayout = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    const recentEntries = statsEntries.filter((entry) => new Date(entry.performed_date) >= start);
    return sumPayout(recentEntries, artistRate);
  }, [statsEntries, artistRate]);

  const currentMonthPayout = useMemo(() => {
    const now = new Date();
    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const monthEntries = statsEntries.filter((entry) => entry.performed_date.startsWith(yearMonth));
    return sumPayout(monthEntries, artistRate);
  }, [statsEntries, artistRate]);

  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const customRangePayout = useMemo(() => {
    if (!customFrom || !customTo) return null;
    const fromDate = customFrom;
    const toDate = customTo;
    if (fromDate > toDate) return null;
    const entries = statsEntries.filter(
      (e) => e.performed_date >= fromDate && e.performed_date <= toDate
    );
    return sumPayout(entries, artistRate);
  }, [statsEntries, artistRate, customFrom, customTo]);

  if (state.loading) {
    return (
      <DashboardShell title="Artist Dashboard">
        <div className="text-gray-400">Φόρτωση λογαριασμού...</div>
      </DashboardShell>
    );
  }

  if (!user) {
    return <Navigate to="/dashboard/login" replace />;
  }

  if (user.role !== 'artist') {
    return <Navigate to="/dashboard" replace />;
  }

  const today = toDateInputValue(new Date());
  const todayEntries = statsEntries.filter((entry) => entry.performed_date === today);
  const todayPayout = sumPayout(todayEntries, artistRate);

  const groupedByDate = groupEntriesByDate(entries);
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

  const pendingEntries = statsEntries.filter((entry) =>
    !entry.payment_id && entry.performed_date >= cycleRange.start && entry.performed_date <= cycleRange.end
  );
  const pendingPayout = sumPayout(pendingEntries, artistRate);

  const bestDay = getBestDayPayout(statsEntries, artistRate);
  const bestMonth = getBestMonthPayout(statsEntries, artistRate);


  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!artistId) return;
    setSubmitting(true);
    setError(null);
    try {
      const costValue = Number(cost);
      if (Number.isNaN(costValue) || costValue <= 0) {
        throw new Error('Enter a valid cost.');
      }
      const performedAt = combineDateTimeAthens(date, time);
      const cashValue = cashAmount ? Number(cashAmount) : 0;
      const cardValue = cardAmount ? Number(cardAmount) : 0;
      const tipValue = tipEur ? Number(tipEur) : 0;
      if (paymentMethod === 'split') {
        if (Number.isNaN(cashValue) || Number.isNaN(cardValue)) {
          throw new Error('Συμπλήρωσε σωστά τα ποσά μετρητών/κάρτας.');
        }
        if (Math.abs((cashValue + cardValue) - costValue) > 0.01) {
          throw new Error('Μετρητά + κάρτα πρέπει να ισούνται με το ποσό.');
        }
      }
      if (Number.isNaN(tipValue) || tipValue < 0) {
        throw new Error('Συμπλήρωσε σωστά το φιλοδώρημα.');
      }
      if (editing) {
        await updateEntry(editing.id, {
          performedAt,
          performedDate: date,
          description,
          costEur: costValue,
          paymentMethod,
          cashAmount: paymentMethod === 'split' ? cashValue : null,
          cardAmount: paymentMethod === 'split' ? cardValue : null,
          ownCustomer,
          workPeriod: ownCustomer ? workPeriod : null,
          tipEur: tipValue,
          note,
        });
        setSuccessMessage(null);
      } else {
        await createEntry({
          artistId,
          performedAt,
          performedDate: date,
          description,
          costEur: costValue,
          paymentMethod,
          cashAmount: paymentMethod === 'split' ? cashValue : null,
          cardAmount: paymentMethod === 'split' ? cardValue : null,
          ownCustomer,
          workPeriod: ownCustomer ? workPeriod : null,
          tipEur: tipValue,
          note,
        });
        setSuccessMessage('Η καταχώρηση προστέθηκε. Κάνε ανανέωση της σελίδας.');
      }
      setDescription('');
      setCost('');
      setPaymentMethod('cash');
      setCashAmount('');
      setCardAmount('');
      setTipEur('');
      setOwnCustomer(false);
      setWorkPeriod('inside');
      setShowSpecial(false);
      setNote('');
      setEditing(null);
    } catch (err: any) {
      setError(err?.message || 'Could not save entry.');
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(entry: TattooEntry) {
    setEditing(entry);
    setDate(entry.performed_date);
    setTime(athensTime(entry.performed_at));
    setDescription(entry.description);
    setCost(String(entry.cost_eur));
    setPaymentMethod(entry.payment_method || 'cash');
    setCashAmount(entry.cash_amount != null ? String(entry.cash_amount) : '');
    setCardAmount(entry.card_amount != null ? String(entry.card_amount) : '');
    setTipEur(entry.tip_eur != null ? String(entry.tip_eur) : '');
    setOwnCustomer(Boolean(entry.own_customer));
    setWorkPeriod(entry.work_period === 'outside' ? 'outside' : entry.work_period === 'external' ? 'external' : 'inside');
    setShowSpecial(Boolean(entry.own_customer));
    setNote(entry.note || '');
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  async function handleDelete(entryId: string) {
    try {
      await deleteEntry(entryId);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete entry.');
    }
  }

  return (
      <DashboardShell title="Πίνακας Καλλιτέχνη" subtitle={`Καλώς ήρθες ${user.displayName}`}>
      {error && <p className="mb-4 text-sm text-red-400">{error}</p>}
      {successMessage && <p className="mb-4 text-sm text-green-400">{successMessage}</p>}

      <section className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Σήμερα</p>
          <p className="text-xl font-semibold">{formatCurrency(todayPayout)}</p>
          <p className="text-xs text-gray-500">Το ποσοστό μου</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Τελευταίες 7 ημέρες</p>
          <p className="text-xl font-semibold">{formatCurrency(recentWeekPayout)}</p>
          <p className="text-xs text-gray-500">Το ποσοστό μου</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Τρέχον μήνα</p>
          <p className="text-xl font-semibold">{formatCurrency(currentMonthPayout)}</p>
          <p className="text-xs text-gray-500">Το ποσοστό μου</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-400">Το ποσοστό μου (υπόλοιπο)</p>
          <p className="text-xl font-semibold">{formatCurrency(pendingPayout)}</p>
          <p className="text-xs text-gray-500">Κύκλος {cycleRange.start} → {cycleRange.end}</p>
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-6 mb-8">
        <div ref={formRef} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">{editing ? 'Επεξεργασία καταχώρησης' : 'Νέα καταχώρηση'}</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Ημερομηνία</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Ώρα</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Σχέδιο / περιγραφή</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Κόστος (€)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} required min="0" step="0.01" className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Τρόπος πληρωμής</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'split')} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white">
                <option value="cash">Μετρητά</option>
                <option value="card">Κάρτα</option>
                <option value="split">Μισά/Μισά</option>
              </select>
            </div>
            {paymentMethod === 'split' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Μετρητά (€)</label>
                  <input type="number" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)} min="0" step="0.01" className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Κάρτα (€)</label>
                  <input type="number" value={cardAmount} onChange={(e) => setCardAmount(e.target.value)} min="0" step="0.01" className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Φιλοδώρημα (δικό μου)</label>
              <input type="number" value={tipEur} onChange={(e) => setTipEur(e.target.value)} min="0" step="0.01" className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div className="rounded-md border border-dashed border-gray-700 p-3">
              <button
                type="button"
                onClick={() => setShowSpecial((prev) => !prev)}
                className="text-xs text-pink-300 hover:text-pink-200"
              >
                Ειδική περίπτωση
              </button>
              {(showSpecial || ownCustomer) && (
                <div className="mt-3 space-y-3">
                  <label className="flex items-start gap-2 text-xs text-gray-300">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={ownCustomer}
                      onChange={(e) => setOwnCustomer(e.target.checked)}
                    />
                    <span>Δικός μου πελάτης (σπάνια περίπτωση)</span>
                  </label>
                  {ownCustomer && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Ωράριο</label>
                      <select value={workPeriod} onChange={(e) => setWorkPeriod(e.target.value as 'inside' | 'outside' | 'external')} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white">
                        <option value="inside">Εντός ωραρίου (60%)</option>
                        <option value="outside">Εκτός ωραρίου (70%)</option>
                        <option value="external">Εξωτερικός Συνεργάτης (75%)</option>
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Σημείωση (προαιρετικό)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div className="flex gap-2">
              <button disabled={submitting} className="bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white font-semibold rounded-md px-4 py-2">
                {editing ? 'Αποθήκευση' : 'Προσθήκη'}
              </button>
              {editing && (
                <button type="button" onClick={() => setEditing(null)} className="border border-gray-700 text-gray-300 rounded-md px-4 py-2">
                  Ακύρωση
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3">Φίλτρα</h2>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Από</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Έως</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Αναζήτηση περιγραφής</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
          </div>
          <div className="mt-4 pt-4 border-t border-gray-800">
            <h3 className="text-sm font-medium text-gray-300 mb-2">Υπολογισμός κερδών για περίοδο</h3>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Από</label>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Έως</label>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
            </div>
            {customFrom && customTo && (
              <p className="text-sm text-gray-300">
                {customFrom > customTo ? (
                  <span className="text-amber-400">Επέλεξε έγκυρη περίοδο (από ≤ έως)</span>
                ) : (
                  <>
                    <span className="text-gray-400">Το ποσοστό μου: </span>
                    <span className="font-semibold text-pink-300">{formatCurrency(customRangePayout ?? 0)}</span>
                  </>
                )}
              </p>
            )}
          </div>
          <div className="mt-4 text-xs text-gray-400 space-y-1">
            <p>Καλύτερη ημέρα: {bestDay.date ? `${bestDay.date} (${formatCurrency(bestDay.total)})` : '—'}</p>
            <p>Καλύτερος μήνας: {bestMonth.month ? `${bestMonth.month} (${formatCurrency(bestMonth.total)})` : '—'}</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-8">
        <h2 className="text-lg font-semibold mb-3">Καταχωρήσεις</h2>
        {loading ? (
          <p className="text-gray-400">Φόρτωση καταχωρήσεων...</p>
        ) : (
          <div className="space-y-4">
            {sortedDates.length === 0 && <p className="text-gray-400">Δεν υπάρχουν καταχωρήσεις.</p>}
            {sortedDates.map((day) => {
              const list = groupedByDate[day];
              const total = sumEntries(list);
              return (
                <div key={day} className="border border-gray-800 rounded-md">
                  <div className="flex items-center justify-between px-4 py-2 bg-black/30 text-sm text-gray-300">
                    <span>{day}</span>
                    <span>Το ποσοστό μου {formatCurrency(sumPayout(list, artistRate))}</span>
                  </div>
                  <div className="divide-y divide-gray-800">
                    {list.map((entry) => (
                      <div key={entry.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <p className="font-medium">{entry.description}</p>
                          <p className="text-xs text-gray-400">
                            {athensTime(entry.performed_at)} · {entry.note || 'Χωρίς σημείωση'} · {entry.payment_method === 'card' ? 'Κάρτα' : entry.payment_method === 'split' ? 'Μισά/Μισά' : 'Μετρητά'}
                            {entry.payment_method === 'split' ? ` (${entry.cash_amount ?? 0}/${entry.card_amount ?? 0})` : ''}
                            {entry.tip_eur ? ` · Φιλοδώρημα ${formatCurrency(entry.tip_eur)}` : ''}
                            {entry.own_customer ? ` · Δικός μου (${entry.work_period === 'external' ? 'εξωτερικός συνεργάτης' : entry.work_period === 'outside' ? 'εκτός' : 'εντός'} ωραρίου)` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span>{formatCurrency(entry.cost_eur)}</span>
                          <button onClick={() => startEdit(entry)} className="text-pink-300 hover:text-pink-200">Επεξεργασία</button>
                          <button onClick={() => setDeleteTarget(entry)} className="text-red-300 hover:text-red-200">Διαγραφή</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm bg-gray-900 border border-gray-800 rounded-xl p-5 shadow-2xl">
            <h3 className="text-lg font-semibold mb-2">Επιβεβαίωση διαγραφής</h3>
            <p className="text-sm text-gray-300 mb-4">
              Θέλεις να διαγράψεις την καταχώρηση "{deleteTarget.description}";
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteTarget(null)} className="border border-gray-700 text-gray-300 rounded-md px-4 py-2">
                Ακύρωση
              </button>
              <button
                onClick={async () => {
                  const id = deleteTarget.id;
                  setDeleteTarget(null);
                  await handleDelete(id);
                }}
                className="bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2"
              >
                Διαγραφή
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="bg-gray-900 border border-gray-800 rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3">Ιστορικό πληρωμών</h2>
        {payments.length === 0 ? (
          <p className="text-gray-400">Δεν υπάρχουν πληρωμές.</p>
        ) : (
          <div className="space-y-2 text-sm">
            {payments.map((payment) => (
              <div key={payment.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between border border-gray-800 rounded-md px-3 py-2">
                <div>
                  <p className="font-medium">{payment.period_start} → {payment.period_end}</p>
                  <p className="text-xs text-gray-400">Πληρώθηκε {new Date(payment.paid_at).toLocaleDateString()}</p>
                </div>
                <div className="text-sm">{formatCurrency(payment.payout_total)}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardShell>
  );
}
